<template>
  <div class="whale-loading-container" :class="{ 'is-fullscreen': fullscreen }">
    <div class="whale-loading-content">
      <!-- 吉祥物与动态微交互 -->
      <div class="whale-mascot-wrapper">
        <div class="whale-mascot">
          <img
            :src="whaleImageSrc"
            alt="鲸夕吉祥物"
            class="whale-img"
            draggable="false"
          />
          <!-- 灵感灯泡微光动画 -->
          <div class="lightbulb-glow" aria-hidden="true"></div>
          <!-- 水柱喷泉气泡动画 -->
          <div class="spout-bubble bubble-1" aria-hidden="true"></div>
          <div class="spout-bubble bubble-2" aria-hidden="true"></div>
        </div>
        <!-- 动态呼吸阴影 -->
        <div class="whale-shadow" aria-hidden="true"></div>
      </div>

      <!-- 斜纹动态进度条 -->
      <div class="progress-bar-wrapper">
        <div class="progress-bar-track">
          <div
            class="progress-bar-fill"
            :style="{ width: `${currentProgress}%` }"
          ></div>
        </div>
      </div>

      <!-- 底部设计小贴士条 -->
      <div class="tips-wrapper">
        <span class="tips-badge">
          <svg class="tips-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 18h6"></path>
            <path d="M10 22h4"></path>
            <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"></path>
          </svg>
          <span>Tips</span>
        </span>
        <span class="tips-text">{{ activeTipText }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import whaleImgUrl from '@/assets/whale-loading.png';

const props = defineProps({
  fullscreen: {
    type: Boolean,
    default: true,
  },
  customTip: {
    type: String,
    default: '',
  },
  progress: {
    type: Number,
    default: null,
  },
});

const whaleImageSrc = whaleImgUrl;

const DEFAULT_TIPS = [
  '按下 Command + G，将选中的多个元素合并为一个群组（Group）',
  '按住 Shift 键拖拽控制点，可等比例缩放图形与文本框',
  '双击画布上的文本图层，可快速进入文字编辑模式',
  '按住 Alt / Option 键拖拽图层，即可快速复制一份',
  '按住空格键并拖拽鼠标，可快速平移画布视野',
  '按下 Command + Z 撤销操作，Command + Shift + Z 重做操作',
  '使用快捷键 [ 和 ] 可以快速调整图层的上下层级',
  '在编辑器左侧素材库，可一键替换模版中的 Logo 与二维码',
  '选中元素后按 Delete 或 Backspace 键可快速删除',
  '双击画布空白区域或按 Esc 键可取消所有元素的选中状态',
];

const currentTipIndex = ref(0);

const currentProgress = ref(props.progress ?? 18);
let progressInterval = null;

const activeTipText = computed(() => {
  if (props.customTip) return props.customTip;
  return DEFAULT_TIPS[currentTipIndex.value] || DEFAULT_TIPS[0];
});

onMounted(() => {
  // 随机选择一条初始 Tip
  currentTipIndex.value = Math.floor(Math.random() * DEFAULT_TIPS.length);

  // 进度条平滑自增动画（模拟载入过程）
  if (props.progress == null) {
    progressInterval = setInterval(() => {
      if (currentProgress.value < 90) {
        currentProgress.value += Math.floor(Math.random() * 8) + 4;
        if (currentProgress.value > 90) currentProgress.value = 90;
      }
    }, 180);
  }
});

onUnmounted(() => {
  if (progressInterval) clearInterval(progressInterval);
});
</script>

<style scoped>
.whale-loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 380px;
  background: #f8fafc;
  user-select: none;
}

.whale-loading-container.is-fullscreen {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: #f8fafc;
}

.whale-loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 90vw;
  animation: content-pop-in 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes content-pop-in {
  0% {
    opacity: 0;
    transform: scale(0.96) translateY(8px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* 吉祥物动画区 */
.whale-mascot-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
}

.whale-mascot {
  position: relative;
  width: 160px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: whale-float 2.4s ease-in-out infinite;
}

.whale-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 4px 12px rgba(15, 23, 42, 0.06));
}

/* 呼吸水花气泡 */
.spout-bubble {
  position: absolute;
  border-radius: 50%;
  background: rgba(34, 197, 94, 0.4);
  pointer-events: none;
}

.bubble-1 {
  width: 8px;
  height: 8px;
  top: 18px;
  left: 88px;
  animation: bubble-float 1.8s ease-in-out infinite;
}

.bubble-2 {
  width: 6px;
  height: 6px;
  top: 8px;
  left: 96px;
  animation: bubble-float 1.8s ease-in-out 0.6s infinite;
}

/* 呼吸阴影 */
.whale-shadow {
  width: 110px;
  height: 12px;
  background: radial-gradient(ellipse at center, rgba(148, 163, 184, 0.35) 0%, rgba(148, 163, 184, 0) 70%);
  border-radius: 50%;
  margin-top: -6px;
  animation: shadow-breathe 2.4s ease-in-out infinite;
}

/* 动画关键帧 */
@keyframes whale-float {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-8px) rotate(-1deg);
  }
}

@keyframes shadow-breathe {
  0%, 100% {
    transform: scale(1);
    opacity: 0.6;
  }
  50% {
    transform: scale(0.82);
    opacity: 0.25;
  }
}

@keyframes bubble-float {
  0% {
    transform: translateY(0) scale(0.8);
    opacity: 0;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    transform: translateY(-14px) scale(1.2);
    opacity: 0;
  }
}

/* 斜纹进度条 */
.progress-bar-wrapper {
  margin-bottom: 24px;
}

.progress-bar-track {
  width: 220px;
  height: 14px;
  border: 1.5px solid #0f172a;
  border-radius: 4px;
  background: #ffffff;
  padding: 1.5px;
  box-sizing: border-box;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.progress-bar-fill {
  height: 100%;
  min-width: 8px;
  background: linear-gradient(90deg, #7c3aed 0%, #3b82f6 100%);
  transition: width 0.2s ease-out;
  border-radius: 2px;
}

/* 底部小贴士条 */
.tips-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #334155;
  padding: 4px 12px;
  max-width: 620px;
  text-align: center;
  line-height: 1.4;
}

.tips-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: #ede9fe;
  border: 1px solid #ddd6fe;
  border-radius: 6px;
  color: #6d28d9;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.tips-icon {
  flex-shrink: 0;
  color: #6d28d9;
}

.tips-text {
  display: inline-block;
  color: #334155;
  white-space: normal;
}

/* Tip 切换动画 */
.tip-fade-enter-active,
.tip-fade-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.tip-fade-enter-from {
  opacity: 0;
  transform: translateY(4px);
}

.tip-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
